/**
 * Holds a just-minted API key until somebody has actually seen it.
 *
 * The reveal cannot live inside the sign-up form. Creating an account sets the
 * session, and every sign-up surface is rendered only while signed OUT — the
 * dashboard switches on `isAuthed`, the modal closes — so the form unmounts in
 * the same tick the key arrives. A reveal held in that component's state is
 * destroyed before it can paint, which is how the key went missing in the first
 * place.
 *
 * So the key is parked here and painted by <NewApiKeyGate/>, which sits above
 * the signed-in/signed-out switch and survives it.
 *
 * sessionStorage rather than a module variable: losing this value is exactly
 * the bug being fixed, and a stray reload would take a module variable with it.
 * It is scoped to this tab, and cleared the moment the reveal is dismissed.
 */
const STORAGE_KEY = 'loreto_fresh_api_key';
const listeners = new Set();

function emit() {
  listeners.forEach((fn) => {
    try { fn(); } catch { /* one bad listener must not break the others */ }
  });
}

export function subscribeFreshKey(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Park a key returned by signup. Pass the whole api_key object. */
export function stashFreshKey(apiKey) {
  if (!apiKey?.key) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(apiKey));
  } catch {
    /* Storage blocked (private mode, or the user disabled it). The gate falls
       back to the in-memory copy below so the reveal still happens. */
  }
  memory = apiKey;
  emit();
}

let memory = null;

export function peekFreshKey() {
  if (memory) return memory;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearFreshKey() {
  memory = null;
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* nothing to clear */ }
  emit();
}
